// Barra de filtros SIMPLIFICADA da Consulta (feedback de usuário:
// "muitos filtros"): Dia (calendário single-day, mesmo padrão do
// dashboard-day-filter) + Customer (autocomplete) + Transação
// (SBC-/ORD-) + Evento (select único) + Agendado (triestado).
// O whitelabel NÃO entra aqui: vem do seletor global
// (useAuth.selectedWhiteLabel), padrão das demais telas. Filtros
// legados de deep-link viram chips (notification-search-applied-chips).
import { enUS, es, ptBR } from "date-fns/locale"
import {
  CalendarClockIcon,
  CalendarIcon,
  SearchIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { ComboBox } from "@/components/ui/combo-box"
import { Calendar } from "@/components/ui/default-calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { ENDPOINTS } from "@/constants"
import { useNotificationCenterNotificationsSearchParams } from "@/hooks"
import { cn } from "@/lib/utils"
import { useQueryClient } from "@/libs"
import { DateHelper } from "@/utils"

import {
  getNotificationEventOriginGroup,
  NOTIFICATION_EVENTS,
} from "../constants"
import { NotificationSearchCustomerFilter } from "./notification-search-customer-filter"

const filtersPrefix =
  "pages.notificationCenter.searchPage.filters"

// Locale do date-fns por idioma do i18n (rótulo do calendário).
const DATE_FNS_LOCALES = {
  "pt-BR": ptBR,
  en: enUS,
  es,
} as const

const FilterField = ({
  id,
  label,
  className,
  children,
}: {
  id: string
  label: string
  className?: string
  children: React.ReactNode
}) => (
  <div
    className={cn(
      "flex min-w-40 flex-col gap-1",
      className
    )}
  >
    <Label
      htmlFor={id}
      className="text-xs text-muted-foreground"
    >
      {label}
    </Label>
    {children}
  </div>
)

// Dia único — mesmo padrão visual do DashboardDayFilter (Popover +
// Calendar + botão "Hoje"), sem faixa de horas.
const DayFilter = () => {
  const { t, i18n } = useTranslation()
  const { date, isToday, setDate, resetToToday } =
    useNotificationCenterNotificationsSearchParams()

  const calendarLocale =
    DATE_FNS_LOCALES[
      i18n.language as keyof typeof DATE_FNS_LOCALES
    ] ?? ptBR

  // O hook garante "yyyy-MM-dd"; meio-dia evita drift de timezone.
  const selectedDate = date
    ? new Date(`${date}T12:00:00`)
    : undefined

  return (
    <div className="flex items-end gap-2">
      <div className="flex min-w-44 flex-col gap-1">
        <Label
          id="notification-search-day-label"
          className="text-xs text-muted-foreground"
        >
          {t(`${filtersPrefix}.day`)}
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              aria-labelledby="notification-search-day-label"
              className="justify-between text-left font-normal capitalize"
            >
              {selectedDate
                ? DateHelper.formatDate(
                    selectedDate,
                    "dd/MM/yyyy"
                  )
                : t(`${filtersPrefix}.dayPlaceholder`)}
              <CalendarIcon className="size-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
          >
            <Calendar
              initialFocus
              mode="single"
              defaultMonth={selectedDate}
              selected={selectedDate}
              className="capitalize"
              locale={calendarLocale}
              // Sem dias futuros — a Consulta olha o realizado.
              disabled={{ after: new Date() }}
              onSelect={(day) => {
                if (!day) return
                setDate(
                  DateHelper.formatDate(day, "yyyy-MM-dd")
                )
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      <Button
        type="button"
        variant={isToday ? "secondary" : "outline"}
        onClick={resetToToday}
        className="gap-1.5"
      >
        <CalendarClockIcon className="size-4" />
        {t(`${filtersPrefix}.todayButton`)}
      </Button>
    </div>
  )
}

// Transação (SBC-/ORD-): o valor vira ?search= — o backend resolve
// o campo (mesma chave usada pelos deep-links antigos).
const TransactionFilter = () => {
  const { t } = useTranslation()
  const { params, setParams } =
    useNotificationCenterNotificationsSearchParams()
  const [term, setTerm] = useState(params.search ?? "")
  // Ressincroniza quando a URL muda por fora (deep-link, chips) —
  // padrão "adjust state during render" (sem useEffect).
  const [lastSearch, setLastSearch] = useState(
    params.search
  )
  if (params.search !== lastSearch) {
    setLastSearch(params.search)
    setTerm(params.search ?? "")
  }

  const commit = () => {
    const trimmed = term.trim()
    if (trimmed === (params.search ?? "")) return
    setParams({ search: trimmed || undefined })
  }

  return (
    <Input
      id="notification-search-transaction"
      value={term}
      onChange={(event) => setTerm(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault()
          commit()
        }
      }}
      placeholder={t(
        `${filtersPrefix}.transactionPlaceholder`
      )}
      aria-label={t(`${filtersPrefix}.transaction`)}
    />
  )
}

// Agendado: triestado Todos / Agendados / Imediatos → ?scheduled.
const ScheduledFilter = () => {
  const { t } = useTranslation()
  const { params, setParams } =
    useNotificationCenterNotificationsSearchParams()

  const options = [
    {
      label: t(`${filtersPrefix}.scheduledAll`),
      value: undefined,
    },
    {
      label: t(`${filtersPrefix}.scheduledOnly`),
      value: "true",
    },
    {
      label: t(`${filtersPrefix}.immediateOnly`),
      value: "false",
    },
  ]

  return (
    <div
      role="group"
      aria-label={t(`${filtersPrefix}.scheduled`)}
      className="flex h-10 items-center gap-1 rounded-md border p-1"
    >
      {options.map((option) => {
        const isActive = params.scheduled === option.value
        return (
          <Button
            key={option.label}
            type="button"
            size="sm"
            variant={isActive ? "secondary" : "ghost"}
            aria-pressed={isActive}
            onClick={() =>
              setParams({ scheduled: option.value })
            }
            className="h-8 px-2.5"
          >
            {option.label}
          </Button>
        )
      })}
    </div>
  )
}

export const NotificationSearchFilters = () => {
  const { t } = useTranslation()
  const { params, setParams } =
    useNotificationCenterNotificationsSearchParams()
  const queryClient = useQueryClient()

  // Os filtros já aplicam sozinhos (URL → React Query). O botão dá um
  // alvo de clique explícito: o clique tira o foco do input de transação
  // (commit no onBlur) e força o refetch da busca, cobrindo o caso "nada
  // mudou, quero buscar de novo".
  const handleSearch = () => {
    queryClient.invalidateQueries({
      queryKey: [
        ENDPOINTS.NOTIFICATION_CENTER_NOTIFICATIONS,
      ],
    })
  }

  // Eventos agrupados por origem (Pedido / Assinatura / Ambos),
  // com "Todos os eventos" para limpar a seleção.
  const eventOptions = [
    {
      label: t(`${filtersPrefix}.allEvents`),
      value: "",
    },
    ...NOTIFICATION_EVENTS.map((event) => {
      const group = getNotificationEventOriginGroup(event)
      return {
        label: `${t(`${filtersPrefix}.originGroup.${group}`)} · ${event.label}`,
        value: event.value,
      }
    }),
  ]

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
      <DayFilter />
      <FilterField
        id="notification-search-customer"
        label={t(`${filtersPrefix}.customer`)}
        className="min-w-56 flex-1"
      >
        <NotificationSearchCustomerFilter />
      </FilterField>
      <FilterField
        id="notification-search-transaction"
        label={t(`${filtersPrefix}.transaction`)}
        className="min-w-56 flex-1"
      >
        <TransactionFilter />
      </FilterField>
      <FilterField
        id="notification-search-event"
        label={t(`${filtersPrefix}.event`)}
        className="min-w-56"
      >
        <ComboBox
          items={eventOptions}
          selected={params.event ?? ""}
          onSelect={(value) =>
            setParams({ event: value || undefined })
          }
          placeholder={t(`${filtersPrefix}.allEvents`)}
          align="start"
        />
      </FilterField>
      <div className="flex flex-col gap-1">
        <Label
          id="notification-search-scheduled-label"
          className="text-xs text-muted-foreground"
        >
          {t(`${filtersPrefix}.scheduled`)}
        </Label>
        <ScheduledFilter />
      </div>
      <Button
        type="button"
        onClick={handleSearch}
        className="ml-auto gap-1.5"
      >
        <SearchIcon className="size-4" />
        {t(`${filtersPrefix}.searchButton`)}
      </Button>
    </div>
  )
}
